import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { IDLE, LoadState, LoaderResult, getErrorKind } from '../utils/loadState';
import { Loader, Query, fetchCached, holdCached, isFresh, peekCached, releaseCached } from '../utils/queryCache';

export type { Loader, Query } from '../utils/queryCache';

const READY: LoadState = { status: 'ready' };
const LOADING: LoadState = { status: 'loading' };

// Caricamento con annullamento e stato. Una richiesta nuova annulla la precedente, così una
// risposta lenta non sovrascrive quella più recente. Durante un nuovo caricamento i dati
// precedenti restano (da mostrare attenuati) per non far saltare la pagina; un errore li toglie.
//
// Con una Query ({ key, load }) la risposta resta in memoria (utils/queryCache): tornando sulla
// pagina compare subito, e uscendo a metà caricamento la richiesta non si butta via.
export function useLoader<T>(source: Loader<T> | Query<T> | null): LoaderResult<T> {
  const initial = typeof source === 'function' || !source ? null : source;
  const [data, setData] = useState<T | null>(() => (initial ? peekCached<T>(initial.key)?.data ?? null : null));
  const [state, setState] = useState<LoadState>(() =>
    initial && peekCached(initial.key) ? (isFresh(initial.key, initial.maxAge) ? READY : LOADING) : IDLE,
  );
  const controllerRef = useRef<AbortController | null>(null);
  const heldKeyRef = useRef<string | null>(null);
  const runIdRef = useRef(0);

  const run = useCallback(
    async (force: boolean) => {
      const runId = ++runIdRef.current;
      const isCurrent = () => runId === runIdRef.current;
      controllerRef.current?.abort();
      controllerRef.current = null;

      const query = typeof source === 'function' || !source ? null : source;
      const key = query?.key ?? null;
      if (heldKeyRef.current !== key) {
        if (heldKeyRef.current) releaseCached(heldKeyRef.current, true);
        heldKeyRef.current = key;
        if (key) holdCached(key);
      }

      if (!source) {
        setState(IDLE);
        setData(null);
        return;
      }

      try {
        let result: T;
        if (query) {
          const cached = peekCached<T>(query.key);
          if (cached) setData(cached.data);
          if (cached && !force && isFresh(query.key, query.maxAge)) {
            setState(READY);
            return;
          }
          setState(LOADING);
          result = await fetchCached(query.key, query.load);
        } else {
          const controller = new AbortController();
          controllerRef.current = controller;
          setState(LOADING);
          result = await (source as Loader<T>)(controller.signal);
        }
        if (!isCurrent()) return;
        setData(result);
        setState(READY);
      } catch (error) {
        if (!isCurrent() || axios.isCancel(error)) return;
        console.error(error);
        setData(null);
        setState({ status: 'error', error: getErrorKind(error) });
      }
    },
    [source],
  );

  useEffect(() => {
    run(false);
  }, [run]);

  useEffect(
    () => () => {
      runIdRef.current += 1;
      controllerRef.current?.abort();
      // Uscendo dalla pagina la richiesta in memoria finisce comunque: se si torna è già pronta
      if (heldKeyRef.current) releaseCached(heldKeyRef.current, false);
      heldKeyRef.current = null;
    },
    [],
  );

  const retry = useCallback(() => {
    run(true);
  }, [run]);

  return { state, data, retry };
}
